import { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface ImageUploadProps {
  type: 'logo' | 'cover' | 'gallery' | 'avatar' | 'event';
  entityId: string;
  currentImage?: string;
  onSuccess: (url: string) => void;
  label?: string;
  aspectRatio?: string;
}

export function ImageUpload({ type, entityId, currentImage, onSuccess, label, aspectRatio = '1/1' }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const inputRef = useRef<HTMLInputElement>(null);

  const getEndpoint = () => {
    switch (type) {
      case 'logo': return `/upload/business/${entityId}/logo`;
      case 'cover': return `/upload/business/${entityId}/cover`;
      case 'gallery': return `/upload/business/${entityId}/gallery`;
      case 'avatar': return `/upload/staff/${entityId}/avatar`;
      case 'event': return `/upload/event/${entityId}/image`;
    }
  };

  const handleFile = async (file: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('La imagen no puede superar 5MB'); return; }

    // Preview inmediato
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('image', file);

      const r = await axios.post(`${API_URL}${getEndpoint()}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      const url = r.data.data.url;
      onSuccess(url);
      toast.success('Imagen subida correctamente');
    } catch {
      toast.error('Error al subir la imagen');
      setPreview(currentImage || null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <label className="label mb-2">{label}</label>}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          position: 'relative',
          aspectRatio: type === 'cover' ? '16/5' : aspectRatio,
          borderRadius: type === 'logo' || type === 'avatar' ? '50%' : '12px',
          overflow: 'hidden',
          border: '2px dashed #e2e8f0',
          background: '#f8fafc',
          cursor: uploading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'border-color .2s',
          maxWidth: type === 'logo' || type === 'avatar' ? '120px' : '100%',
          maxHeight: type === 'cover' ? '200px' : undefined,
        }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = '#3b82f6')}
        onMouseLeave={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
      >
        {preview ? (
          <>
            <img src={preview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: 0, transition: 'opacity .2s',
            }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
            >
              <span style={{ color: 'white', fontSize: '.8rem', fontWeight: 600 }}>
                {uploading ? '⏳ Subiendo...' : '📷 Cambiar'}
              </span>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
            {uploading ? (
              <div>
                <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 8px' }}></div>
                <span style={{ fontSize: '.78rem' }}>Subiendo...</span>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>📷</div>
                <div style={{ fontSize: '.78rem', fontWeight: 600 }}>Subir imagen</div>
                <div style={{ fontSize: '.7rem', marginTop: 4 }}>JPG, PNG · Max 5MB</div>
              </>
            )}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
    </div>
  );
}

// Componente para galería múltiple
interface GalleryUploadProps {
  businessId: string;
  gallery: string[];
  onUpdate: (gallery: string[]) => void;
}

export function GalleryUpload({ businessId, gallery, onUpdate }: GalleryUploadProps) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('image', file);
      const r = await axios.post(`${API_URL}/upload/business/${businessId}/gallery`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUpdate(r.data.data.gallery);
      toast.success('Foto añadida a la galería');
    } catch {
      toast.error('Error al subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (url: string) => {
    if (!confirm('¿Eliminar esta foto?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.delete(`${API_URL}/upload/business/${businessId}/gallery`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { url },
      });
      onUpdate(r.data.data.gallery);
      toast.success('Foto eliminada');
    } catch {
      toast.error('Error al eliminar la foto');
    }
  };

  return (
    <div>
      <label className="label mb-3">Galería de fotos</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 10 }}>
        {gallery.map((img, i) => (
          <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <img src={img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => handleDelete(img)}
              style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(239,68,68,.9)', color: 'white', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: '.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >✕</button>
          </div>
        ))}
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          style={{ aspectRatio: '1', borderRadius: 10, border: '2px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc', flexDirection: 'column', gap: 4 }}
        >
          {uploading ? (
            <div style={{ width: 24, height: 24, border: '2px solid #e2e8f0', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          ) : (
            <>
              <span style={{ fontSize: '1.4rem' }}>+</span>
              <span style={{ fontSize: '.65rem', color: '#94a3b8', fontWeight: 600 }}>Añadir</span>
            </>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
    </div>
  );
}
